import { useQuery } from '@apollo/client'
import { Button, useMediaQuery, useTheme } from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import Pagination from '@material-ui/lab/Pagination'
import React, { useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { ListFilter } from '../../components/ListFilter'
import {
  PROMOTIONS_QUERY,
  PromotionListData,
  PromotionVariables
} from '../../graphql/promotions'
import { getTranslate, lang } from '../../localization'
import PromotionsFilter, {
  EMPTY_PROMOTION_FILTERS,
  PromotionFilters
} from './PromotionsFilter'
import PromotionsHeader from './PromotionsHeader'
import { PromotionsTable } from './PromotionsTable'
import { PromotionsTableMobile } from './PromotionsTableMobile'
import useStyles from './styles/index.styles'

const PER_PAGE = 10

/**
 * Filters live in the URL so a filtered list is shareable and survives reload — the same
 * `?filter=<encoded JSON>` shape BookingList uses (BookingList/index.tsx:49-56).
 *
 * Parsing is defensive: the value is user-editable text in an address bar, so a malformed or
 * hand-truncated payload must fall back to "no filters" rather than throwing and blanking the
 * screen.
 */
const readFiltersFromUrl = (search: string): PromotionFilters => {
  const raw = new URLSearchParams(search).get('filter')
  if (!raw) return EMPTY_PROMOTION_FILTERS
  try {
    const parsed = JSON.parse(decodeURIComponent(raw))
    return {
      providerCode:
        typeof parsed?.providerCode === 'string' ? parsed.providerCode : '',
      status: typeof parsed?.status === 'string' ? parsed.status : ''
    }
  } catch {
    return EMPTY_PROMOTION_FILTERS
  }
}

const Promotions: React.FC = () => {
  const classes = useStyles()
  const theme = useTheme()
  const history = useHistory()
  const location = useLocation()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  // Memoised on `location.search`, NOT recomputed per render. readFiltersFromUrl returns a
  // fresh object literal whenever the URL carries a filter, and PromotionsFilter resyncs its
  // draft on `[applied]` by reference — so an unstable reference made every unrelated re-render
  // (pagination, the Filter toggle, a query settling) silently overwrite whatever the operator
  // was mid-way through typing. Pinned by "keeps an in-progress filter edit across an unrelated
  // re-render" in index.test.tsx, which fails without this.
  //
  // It does NOT cause extra network requests: Apollo compares `variables` structurally, not by
  // reference (@apollo/client/react/hooks/useQuery.js gates reobserve() behind a deep `equal`),
  // so the instability stopped at render. Render-only, but user-visible.
  const applied = useMemo(
    () => readFiltersFromUrl(location.search),
    [location.search]
  )
  const hasFilters = Boolean(applied.providerCode || applied.status)

  // Open on arrival only when filters are already in effect, so the user can see what is
  // narrowing the list they are looking at.
  const [filterOpen, setFilterOpen] = useState(hasFilters)
  const [page, setPage] = useState(1)
  const [nameQuery, setNameQuery] = useState('')

  const { data, loading, error, refetch } = useQuery<
    PromotionListData,
    PromotionVariables
  >(PROMOTIONS_QUERY, {
    // No filter defaults: the list loads the whole (paginated) set for every role, and filters
    // only ever narrow it. See portal-ux-conventions.md section 1.
    variables: {
      providerCode: applied.providerCode || undefined,
      status: applied.status || undefined,
      page,
      perPage: PER_PAGE
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  })

  const applyFilters = (filters: PromotionFilters) => {
    setPage(1)
    const isEmpty = !filters.providerCode && !filters.status
    history.push(
      isEmpty
        ? `/${lang}/promotions`
        : `/${lang}/promotions?filter=${encodeURIComponent(
            JSON.stringify(filters)
          )}`
    )
  }

  const loadedRows = data?.promotions?.promotions || []

  /*
   * Search by name, over the rows ALREADY LOADED.
   *
   * The `promotions` query takes providerCode, status, page and perPage — there is no name
   * argument, so a server-side search would need the BFF and the promotion service to grow
   * one. This is the same client-side shape the notification lists use, and it borrows their
   * component so the wording, the live region and the "loaded" phrasing stay identical.
   *
   * "loaded", never "total": this filters the current page, and saying otherwise would claim
   * a search of the whole set that is not happening. An operator on page 3 sees page 3.
   */
  const query = nameQuery.trim().toLowerCase()
  const rows = query
    ? loadedRows.filter((promotion) =>
        promotion.name.toLowerCase().includes(query)
      )
    : loadedRows

  const pageCount = Math.max(
    1,
    Math.ceil((data?.promotions?.total || 0) / PER_PAGE)
  )

  return (
    <div>
      <PromotionsHeader
        filterOpen={filterOpen}
        toggleFilter={() => setFilterOpen((open) => !open)}
      />
      <PromotionsFilter
        open={filterOpen}
        applied={applied}
        onApply={applyFilters}
      />
      <ListFilter
        label="Promotions"
        value={nameQuery}
        onChange={setNameQuery}
        shown={rows.length}
        loaded={loadedRows.length}
      />
      {error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              {getTranslate('Retry')}
            </Button>
          }>
          {getTranslate('Could not load promotions.')}
        </Alert>
      )}
      {!isSmallScreen ? (
        <PromotionsTable
          data={rows}
          loading={loading}
          reloadData={() => refetch()}
        />
      ) : (
        <PromotionsTableMobile
          data={rows}
          loading={loading}
          reloadData={() => refetch()}
        />
      )}
      <div className={classes.paginationContainer}>
        <Pagination
          count={pageCount}
          color="primary"
          size={isSmallScreen ? 'small' : 'medium'}
          page={page}
          getItemAriaLabel={(type, pageNumber) =>
            getTranslate(
              type === 'page'
                ? 'Go to page {page}'
                : type === 'next'
                ? 'Go to next page'
                : type === 'previous'
                ? 'Go to previous page'
                : type,
              { page: pageNumber }
            )
          }
          onChange={(_, value) => setPage(value)}
        />
      </div>
    </div>
  )
}

export default Promotions
